import React, { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Server, HardDrive, Network, Zap, Hammer, Activity, ShieldCheck } from "lucide-react";

export const IndustriesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const industries = [
    {
      id: "datacenter",
      title: "Hyper-scale Data Centers",
      description: "Direct talent alignment for gigawatt-scale, multi-MW hyperscale networks powering global technology and tier-1 compute clouds.",
      imgUrl: "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg", // verified server rack image
      icon: Server,
      colSpan: "md:col-span-2 md:row-span-1"
    },
    {
      id: "ai",
      title: "AI & Liquid Clusters",
      description: "Sourcing elite hardware cluster architects, liquid cooling specialists, and high-frequency network deployment teams.",
      imgUrl: "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg", // verified server room image with emerald feel
      icon: Cpu,
      colSpan: "md:col-span-1 md:row-span-1"
    },
    {
      id: "cloud",
      title: "Enterprise Cloud Computing",
      description: "Recruiting multi-region network engineers, virtualization leads, and edge caching deployment experts.",
      imgUrl: "https://images.unsplash.com/photo-1570470836811-78ef04bb23d3?crop=entropy&cs=srgb&fm=jpg&q=85",
      icon: HardDrive,
      colSpan: "md:col-span-1 md:row-span-1"
    },
    {
      id: "telecom",
      title: "Telecommunications & Optics",
      description: "Dense fiber networks, terrestrial optical transit pipelines, and satellite gateway engineering pipelines.",
      imgUrl: "https://images.unsplash.com/photo-1760386129108-d17b9cdfc4fa?crop=entropy&cs=srgb&fm=jpg&q=85",
      icon: Network,
      colSpan: "md:col-span-2 md:row-span-1"
    },
    {
      id: "power",
      title: "Critical Infrastructure Power",
      description: "Substation commissioning engineers, high voltage technicians, and critical backup power generator specialists.",
      imgUrl: "https://images.unsplash.com/photo-1759365400065-ab88a7b8e82f?crop=entropy&cs=srgb&fm=jpg&q=85",
      icon: Zap,
      colSpan: "md:col-span-1 md:row-span-1"
    },
    {
      id: "commissioning",
      title: "Level 1-5 Commissioning",
      description: "Complete testing, operational assurance, ASHRAE guidelines validation, and client design specifications compliance.",
      imgUrl: "https://images.pexels.com/photos/10142683/pexels-photo-10142683.jpeg",
      icon: Activity,
      colSpan: "md:col-span-1 md:row-span-1"
    },
    {
      id: "construction",
      title: "Critical Facility Construction",
      description: "Project directors, facility managers, and safety compliance supervisors experienced in major technology rollouts.",
      imgUrl: "https://images.unsplash.com/photo-1762289581607-fc292299dc87?crop=entropy&cs=srgb&fm=jpg&q=85",
      icon: Hammer,
      colSpan: "md:col-span-1 md:row-span-1"
    },
    {
      id: "networking",
      title: "High-Performance Networking",
      description: "Switching routing infrastructure architects and core security team deployment experts.",
      imgUrl: "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg",
      icon: ShieldCheck,
      colSpan: "md:col-span-1 md:row-span-1"
    }
  ];

  return (
    <section 
      id="industries"
      data-testid="industries-section"
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16"
    >
      <div className="mb-10">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#65FFB6]">
          MARKET VERTICALS
        </span>
        <h2 className="text-3xl md:text-5xl font-outfit font-bold tracking-tight text-white mt-3">
          Sectors We Secure. <br />
          <span className="text-emerald-gradient">Uptime Guaranteed.</span>
        </h2>
        <p className="text-slate-400 font-manrope text-base max-w-2xl mt-4">
          NovaCore recruits across all key segments of physical and optical infrastructure. 
          Our candidates ensure that next-generation systems never fail.
        </p>
      </div>

      {/* Bento Grid (Tetris Layout) */}
      <div 
        data-testid="industries-bento-grid"
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {industries.map((ind, idx) => {
          const IconComponent = ind.icon;
          const isHovered = hoveredCard === ind.id;
          const fromLeft = idx % 2 === 0;

          return (
            <motion.div
              key={ind.id}
              className={ind.colSpan}
              initial={{ opacity: 0, x: fromLeft ? -90 : 90 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <div
                data-testid={`industry-card-${ind.id}`}
                onMouseEnter={() => setHoveredCard(ind.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`relative rounded-3xl overflow-hidden glass-panel group transition-all duration-300 h-full min-h-[250px] flex flex-col justify-end p-6 md:p-8 cursor-pointer border border-white/10 ${
                  isHovered ? "border-emerald-500/40 shadow-[0_0_30px_rgba(101,255,182,0.12)] -translate-y-1" : ""
                }`}
              >
                {/* Back Image with Dark overlay */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                  style={{ backgroundImage: `url(${ind.imgUrl})` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30 transition-all duration-300"></div>

                {/* Glowing Tracking Beam Border (Left-side line that lights up on hover) */}
                <div className={`absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-[#65FFB6] to-transparent transition-all duration-500 ${
                  isHovered ? "opacity-100 h-full" : "opacity-0 h-0"
                }`}></div>

                {/* Card Contents */}
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-black/50 border border-white/10 flex items-center justify-center mb-4 transition-colors duration-300 group-hover:border-[#65FFB6]/40">
                    <IconComponent className="w-5 h-5 text-[#65FFB6]" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-outfit font-semibold text-white transition-colors duration-200 group-hover:text-[#65FFB6]">
                    {ind.title}
                  </h3>
                  <p className="text-sm font-manrope text-slate-300 mt-2.5 leading-relaxed">
                    {ind.description}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
